import { SubscriptionService } from '@/services/subscription-service';
import { Ranking, defaultFieldTypeMap } from '@patsnap-biz/refine-filter/src/Ranking';
import { fetchFilterInit, COMBINATION_FIELDS_HASH } from '@/apis/search-result/filter-init';
import { FilterInitResponse, RankingParams } from '@/@dtos/search-result.dto';
import _ from 'lodash';
import { getRanking } from '@/apis/search-result/ranking';
import type VueI18n from 'vue-i18n';
import { SF_FACET } from 'library-@/constants/index';
import retry from '@/utils/retry';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { SearchModeService } from '@/plugins/search-mode/search-mode-service';

const i18n_namespace = 'commonPatent.filterFields';

interface RefineServiceOptions {
    limit: number;
    sort: string;
    _type: string;
    q: string;
    efq: string;
}

export default class RefineService extends SubscriptionService {
    uuid: string;

    preOpenNth: number;

    rankingConfs: Ranking.Conf;

    ready: Error | unknown;

    serviceOptions: RefineServiceOptions;

    semanticFilterQueryList: object[];

    private initDataResp?: FilterInitResponse;

    i18n: VueI18n | undefined;

    sortConfig: {
        displayList: Ranking.FieldKey[],
        hiddenList: Ranking.FieldKey[]
    } = { displayList: [], hiddenList: [] };
    
    private _searchModeService: SearchModeService;

    constructor(options: RefineServiceOptions) {
        super();
        this.uuid = _.uniqueId('RefineService:');
        this.ready = false;
        this.preOpenNth = 1;
        this.serviceOptions = { ...options };
        this.semanticFilterQueryList = [];
        this.rankingConfs = {
            rankingData: {},
            collapseItems: [],
            externalFieldsQueries: [],
            fieldTypeMap: defaultFieldTypeMap
        };
        this._searchModeService = injectService<SearchModeService>(SearchModeService);
    }

    static hasRankingDataPreparedByFieldKey(d, fieldKey) {
        return _.get(d, `${fieldKey}.field`) === fieldKey && Array.isArray(_.get(d, `${fieldKey}.value`));
    }

    static externalFieldsQueries2EfqString = externalFieldsQueries2EfqString;

    get displayList() {
        return [...this.sortConfig.displayList] || [];
    }

    get hiddenList() {
        return [...this.sortConfig.hiddenList] || [];
    }

    async getLeftFilterInitData(queryState) {
        const initData = await fetchFilterInit(queryState);
        const externalFieldsQueries = _.get(initData, 'externalFieldsQueries', []);
        let semanticFilterData = externalFieldsQueries.filter(item => item.field === SF_FACET);
        initData.externalFieldsQueries = externalFieldsQueries.filter(item => item.field !== SF_FACET);
        return [initData, semanticFilterData];
    }

    async init() {
        try {
            const data = await this.getLeftFilterInitData(this.serviceOptions);
            this.initDataResp = data[0];
            this.semanticFilterQueryList = data[1];
            this.updateFieldSortConfig();
            this.updateFieldTypeMap();

            this.updateRankingStatus();
            this.updateCollapseItems();

            await this.postProcessCollapseItems();

            this.ready = true;
        } catch (err) {
            console.error(err);
            this.ready = err;
        }
    }

    // 顶上的正在过滤展示
    updateRankingStatus() {
        this.rankingConfs.externalFieldsQueries = [...(this.initDataResp?.externalFieldsQueries || [])];
    }

    async updateExternalFieldsQueries(options: RefineServiceOptions) {
        const data = await this.getLeftFilterInitData(options);
        const initDataResp = data[0];
        this.semanticFilterQueryList = data[1];
        this.rankingConfs.externalFieldsQueries = [...(initDataResp?.externalFieldsQueries || [])];
        this.serviceOptions = { ...options };
        this.rankingConfs.rankingData = {};
        this.updateCollapseItems();
    }

    public updateFieldSort(option: { displayList: Ranking.FieldKey[], hiddenList: Ranking.FieldKey[] }) {
        const { displayList, hiddenList } = option;
        this.sortConfig = {
            ...this.sortConfig,
            hiddenList,
            displayList,
        };

        this.updateCollapseItems();
        this.postProcessCollapseItems();
    }

    emptySemanticFilterData() {
        this.semanticFilterQueryList = [];
    }

    async getRankingData(fieldKey: Ranking.FieldKey): Promise<Ranking.Field> {
        const search_mode = await this._searchModeService.searchMode;
        const params = {
            field: fieldKey,
            limit: this.serviceOptions.limit,
            sort: this.serviceOptions.sort,
            _type: this.serviceOptions._type,
            q: this.serviceOptions.q,
            efq: this.serviceOptions.efq,
            desc: true,
            use_async: true,
            search_mode,
        };
        const getRankingWithRetry = retry(getRanking, {
            resultParamToAdd: ['bad_query_job_id'],
            isArrayData: true,
            untilFn(data) {
                // 直到接口中不返回bad_query_job_id
                if (data[0].bad_query_job_id) console.log('try again');
                return !data[0].bad_query_job_id;
            }
        });
        return getRankingWithRetry(new RankingParams(params)).execute().then(d => d[0]);
    }

    private updateCollapseItems() {
        const i18n = this.i18n as VueI18n;
        this.rankingConfs.collapseItems = (this.displayList || []).map((fieldKey: Ranking.FieldKey) => {
            const item: SearchResult.FilterViewItem = this.initDataResp?.filterStructureByFieldKey[fieldKey];
            const title = (i18n.te(`${i18n_namespace}.${item.field}`) ? i18n.t(`${i18n_namespace}.${item.field}`) : item.field) as string;
            return { key: item.field, title, tooltip: item.tooltip || undefined, context: { ...item } };
        });

        this.rankingConfs.collapseItems = this.rankingConfs.collapseItems.map((item: Ranking.CollapseItem, index) => {
            let selectedCount = 0;
            const { externalFieldsQueries } = this.rankingConfs;
            _.forEach(externalFieldsQueries, (queryItem) => {
                const { field, childField, type } = queryItem;
                let key = COMBINATION_FIELDS_HASH[field] || field;
                if (key === 'MIPC_FACET') {
                    key = 'MIPC';
                }
                if ((key === item.key || childField === item.key) && type === 'refine') {
                    // 外部跳转过来数据格式有差异，需要做兼容
                    // 正常数据格式为[{ name: '1920' }, { name: '2019' }] 或 ['1920', '2019']
                    // 需特殊处理 ['1920', '2019', { name: '1920' }, { name: '2019' }]
                    const commonEfq = [];
                    const specialEfq = [];
                    // @ts-ignore
                    queryItem.value.forEach(data => (_.isString(data) || _.isNumber(data) ? specialEfq : commonEfq).push(data));
                    selectedCount += commonEfq.length + (Math.ceil(specialEfq.length / 2));

                    //专利价值不用显示选中的数量，见TOC-32577
                    if (this.initDataResp?.fieldTypeMap[key] === Ranking.FieldType.range) {
                        selectedCount = 0;
                    }
                }
            });
            return { ...item, selectedCount };
        });
    }

    private async postProcessCollapseItems() {
        if (!(this.initDataResp)) {
            return;
        }
        const inEfqFields: Set<Ranking.FieldKey> = new Set<Ranking.FieldKey>();
        this.rankingConfs.externalFieldsQueries.forEach(item => inEfqFields.add(item.field));
        this.rankingConfs.collapseItems = this.rankingConfs.collapseItems.map((item: Ranking.CollapseItem, index) => {
            let isOpened;
            // if (inEfqFields.size) {
            //     isOpened = inEfqFields.has(item.key);
            // } else {
            isOpened = index < this.preOpenNth;
            // }
            return { ...item, isOpened };
        });
        this.preOpenNth = -Infinity;
    }

    updateFieldSortConfig() {
        const displayList: string[] = [];
        const hiddenList: string[] = [];
        let collapseItems = this.initDataResp?.filterStructureByFieldKey;
        _.forEach(collapseItems, (item: any) => {
            const index: any = this.initDataResp?.displayedFilters.indexOf(item.field);
            if (index > -1) {
                displayList[index] = item.field;
            } else {
                hiddenList.push(item.field);
            }
        });
        this.sortConfig = {
            displayList: _.filter(displayList),
            hiddenList
        };
    }

    private updateFieldTypeMap() {
        if (!this.initDataResp || !this.initDataResp.fieldTypeMap) { return; }
        Object.assign(this.rankingConfs.fieldTypeMap, this.initDataResp.fieldTypeMap);
        Object.keys(this.initDataResp.filterStructureByFieldKey).forEach((fieldKey) => {
            // 默认没制定的用classification
            if (!this.rankingConfs.fieldTypeMap[fieldKey]) {
                this.rankingConfs.fieldTypeMap[fieldKey] = Ranking.FieldType.classification;
            }
        });

    }

    async updateSearchRanking(fieldKey: Ranking.FieldKey) {
        if (this.rankingConfs.fieldTypeMap[fieldKey] === 'rangeTypes') {
            // range类型的不用请求接口
            return;
        }
        try {
            const fieldData = await this.getRankingData(fieldKey);

            this.rankingConfs.rankingData = {
                ...this.rankingConfs.rankingData,
                [fieldKey]: fieldData
            };
        } catch (e) {
            this.rankingConfs.rankingData = {
                ...this.rankingConfs.rankingData,
                [fieldKey]: {
                    'field': fieldKey,
                    'value': []
                }
            };
        }
    }

    cleanCachedRankingByField(fieldKey: Ranking.FieldKey) {
        delete this.rankingConfs.rankingData[fieldKey];
    }
}


// efq的各种不同类型的filter 拼接
function externalFieldsQueries2EfqString(externalFieldsQueries: Ranking.ExternalFieldsQueries): string {
    let efqString = '';
    externalFieldsQueries.forEach(item => {
        const { value, field, childField } = item;
        if (!value.length) return;

        let efqItem = '';
        // efq结构能够保证，全对象数组 或者是 字符串数组
        if (value.every(item => typeof item === 'object')) {
            const efqItemValueString = _.compact(value)
                // 需要加上引号
                .map((item) => `"${item.name.replace(/"/g, '\\"')}"`)
                .join(' OR ');
            efqItem = `${childField || field}:(${efqItemValueString})`;
        } else {
            const [min, max] = value;
            efqItem = `${field}:[${min} TO ${max}]`;
        }

        efqString += (item.type === 'refine' ? ' AND ' : ' NOT ') + efqItem;
    });
    let efqStringOri = efqString.replace(/^ AND /, '').trim();

    return efqStringOri;
}
